<template>
  <div
    class="banner"
    @click="()=>onClickBanner(false)"
  >
    <div class="info">
      <JumpMiniApp
        v-if="miniAppPathClickAvator"
        :path="miniAppPathClickAvator"
        @launch="()=>onClickBanner(true)"
      />
      <div>
        <i
          v-if="person?.avatar"
          class="avatar"
          :style="{ backgroundImage: getImage() }"
        />
        <i
          v-else
          class="logo"
        />
      </div>
      <slot>
        <div class="detail">
          <div
            v-if="person?.nick"
            class="person"
          >
            <div
              v-if="showNick"
              class="nick"
            >
              {{ person?.nick }}
            </div>
            <div class="banner-txt">
              {{ bannerTxt || "“发现了一条视频，邀你同看”" }}
            </div>
          </div>
          <div
            v-else
            class="banner-txt"
          >
            {{ bannerTxt || "有趣的人都在微视" }}
          </div>
        </div>
      </slot>
    </div>
    <div class="btn">
      {{ btnTxt || "打开看看" }}
      <JumpMiniApp
        v-if="miniAppPathClickLook"
        :path="miniAppPathClickLook"
        @launch="()=>onClickBanner(true)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed } from 'vue';
import { fixHttps } from '@/utils/no-import-meta/url';
import { getSchemaFromExposureMaterial } from '@/utils/schema';
import { changeShareMaskText, TEXT } from '@/utils/share';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import { useBannerReport } from '@/composable/use-compoent-report-methods';
import useProvideBrowserInfo from '@/composable/use-provide-browser-info';
import { useStore } from '@/store/index';
import { MetaPerson, FeedReportOpt } from '@/types/feed';
import { ModelExposureMaterialInfo } from '@/types/model';
import JumpMiniApp from './jump-mini-app.vue';

const { openSchemaAppendParamsClipboard } = useGetCommonMethods();
const { info: urlInfo } = useProvideUrlInfo();
const { info: browserInfo } = useProvideBrowserInfo();
const { state } = useStore();
const shareUi = computed(() => state.shareUi);

interface Props {
  person: MetaPerson | null,
  bannerTxt: string,
  btnTxt: string,
  showNick: boolean,
  miniAppPathClickLook: string,
  miniAppPathClickAvator: string
  exposureMaterialInfo: ModelExposureMaterialInfo,
  activeFeedReportOpt: FeedReportOpt
}
const props = withDefaults(defineProps<Props>(), {
  showNick: true,
});
const {
  miniAppPathClickAvator,
  showNick,
  miniAppPathClickLook,
  person,
  bannerTxt,
  btnTxt,
  exposureMaterialInfo,
  activeFeedReportOpt,
} = toRefs(props);
const isSupportExposureMaterial = computed(() => {
  const { materialId, exposureMaterialName } = exposureMaterialInfo.value ?? {};
  return !!(exposureMaterialName || materialId);
});
const bannerReport = useBannerReport({
  btnTxt,
  activeFeedReportOpt,
  exposureMaterialInfo,
  isSupportExposureMaterial,
});
bannerReport.mountedReport();
const getImage = () => `url(${fixHttps(person.value?.avatar ?? '')})`;
const onClickBanner = (clickMiniApp: boolean) => {
  bannerReport.onClickBanner();
  if (clickMiniApp) return;
  const { id: feedId, channel } = urlInfo.query;
  if (!channel && browserInfo.client === 'wx') {
    // 代表复制链接过来的且处于微信中
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }
  if (isSupportExposureMaterial.value) {
    const schema = getSchemaFromExposureMaterial(exposureMaterialInfo.value, 'banner');
    openSchemaAppendParamsClipboard(true, `${schema}&session_from=10000018`);
  } else {
    // banner打开当前视频，不随精选集变滑动而变化
    openSchemaAppendParamsClipboard(true, `weishi://feed?feed_id=${feedId}&session_from=10000018`);
  }
};
</script>

<style lang="scss" scoped>
.banner {
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  @include responsiveStyleSingle(height, 16, 'vw');
  left: 0;
  top: 0;
  @include responsiveStyleSingle(padding-left, 4.2667, 'vw');
  @include responsiveStyleSingle(padding-right, 4.2667, 'vw');
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  .info {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    overflow: hidden;
    i {
      display: block;
      @include responsiveStyleSingle(margin-right, 2.6667, 'vw');
    }
    .logo {
      @include responsiveStyleSingle(width, 9.6, 'vw');
      @include responsiveStyleSingle(height, 9.6, 'vw');
      background-image: url("https://isee.weishi.qq.com/iseecdn/static/img/icon-ws-72.png");
      background-size: 100% 100%;
    }
    .avatar {
      @include responsiveStyleSingle(width, 10.6667, 'vw');
      @include responsiveStyleSingle(height, 10.6667, 'vw');
      border-radius: 50%;
      background-size: 100% 100%;
    }

    .detail {
      flex: 1;
      overflow: hidden;
      .banner-txt {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        @include responsiveStyleSingle(font-size, 3.7333, 'vw');
        font-weight: bold;
      }
      .person {
        .nick {
          @include responsiveStyleSingle(margin-bottom, 1.0667, 'vw');
          @include responsiveStyleSingle(line-height, 5.3333, 'vw');
          @include responsiveStyleSingle(font-size, 3.7333, 'vw');
          font-weight: bold;
        }
        .banner-txt {
          @include responsiveStyleSingle(font-size, 3.2, 'vw');
          font-weight: normal;
          @include responsiveStyleSingle(line-height, 4.2667, 'vw');
        }
      }
    }
  }
  .btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    @include responsiveStyleSingle(width, 23.4667, 'vw');
    @include responsiveStyleSingle(height, 8.5333, 'vw');
    @include responsiveStyleSingle(margin-left, 1, 'vw');
    @include responsiveStyleSingle(font-size, 3.7333, 'vw');
    font-weight: bold;
    background: #7a46ff;
    @include responsiveStyleSingle(border-radius, 4.2667, 'vw');
  }
}
</style>
